/**
 * Reading the Stripe / emailed "here is your order" deep link.
 *
 * Web: `https://ziawellnav.com/?guest_paid=1&service=hotshots&request_id=…&email=…`
 * Native: Stripe returns to `/guest-paid-return.html`, which bounces into
 * `com.ziawellnav.app://guest-paid?…` so the receipt stays in the hub, not Safari.
 *
 * This URL is produced in two places — Stripe's success redirect and the
 * confirmation email — and it has to be understood in two places: LandingPage
 * (guests) and the app shell (signed-in customers). App.tsx only renders
 * LandingPage when NOT authenticated, so for a long time a signed-in customer
 * who paid, or who clicked the link in their email, landed in the app shell
 * where nothing read these params at all. No receipt, ever.
 *
 * One parser, so the two callers cannot drift on which key means what.
 */
import { isIosNativeAppRuntime } from './runtimeCapabilities';

export type GuestPaidReturn = {
  /** Normalised service line: hotshots | grocery | tacos | water | waste_management */
  service: string;
  requestId: string | null;
  email: string | null;
};

export const GUEST_PAID_APP_SCHEME = 'com.ziawellnav.app';
export const GUEST_PAID_APP_HOST = 'guest-paid';
export const GUEST_PAID_BOUNCE_PATH = '/guest-paid-return.html';
export const PUBLIC_WEB_ORIGIN = 'https://ziawellnav.com';
export const ZIA_GUEST_PAID_EVENT = 'zia-guest-paid';
export const ZIA_GUEST_PAID_CANCEL_EVENT = 'zia-guest-paid-cancel';

const SESSION_ID_KEY = 'zia_guest_service_checkout';
const SESSION_EMAIL_KEYS = [
  'zia_guest_paid_email',
  'zia_guest_grocery_email',
  'zia_guest_food_email',
  'zia_guest_hotshot_email',
  'zia_guest_water_email',
  'zia_guest_wm_email',
  'zia_guest_semi_email',
  'zia_guest_welding_email',
  'zia_guest_porta_email',
];

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Normalise the `service` param the hubs write. */
export function normalizePaidService(raw: unknown): string {
  const s = String(raw || '').toLowerCase().replace(/[\s-]+/g, '_');
  if (s === 'hotshot' || s === 'hot_shot' || s === 'hot_shots') return 'hotshots';
  if (s === 'food' || s === 'taco' || s === 'tacos_deliver') return 'tacos';
  if (s === 'groceries') return 'grocery';
  if (s === 'wm' || s === 'dumpster' || s === 'dumpsters' || s === 'waste') return 'waste_management';
  if (s === 'water_delivery' || s === 'pallet_water') return 'water';
  if (s === 'semi' || s === 'semi_wide' || s === 'wide_load') return 'semis';
  if (s === 'welder' || s === 'weld') return 'welding';
  if (
    s === 'porta'
    || s === 'portapotty'
    || s === 'porta_pottie'
    || s === 'portable_toilet'
    || s === 'portable_restroom'
  ) return 'porta_potty';
  return s;
}

/**
 * Returns the paid-return details, or null when this is an ordinary visit.
 *
 * Falls back to sessionStorage for the id and email because the URL can lose
 * them — an email client that strips query params, or a customer who reloads
 * after we cleaned the address bar.
 */
export function readGuestPaidReturn(search?: string): GuestPaidReturn | null {
  if (typeof window === 'undefined') return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search ?? window.location.search);
  } catch {
    return null;
  }
  if (params.get('guest_paid') !== '1') {
    if (params.get('water_paid') === '1') {
      const requestId = (params.get('order') || params.get('request_id') || readSession(SESSION_ID_KEY) || '').trim() || null;
      const email = (params.get('email') || readSession('zia_guest_water_email') || readSession('zia_guest_paid_email') || '').trim().toLowerCase() || null;
      return { service: 'water', requestId, email };
    }
    if (params.get('wm_paid') === '1') {
      const requestId = (params.get('job') || params.get('request_id') || readSession(SESSION_ID_KEY) || '').trim() || null;
      const email = (params.get('email') || readSession('zia_guest_wm_email') || readSession('zia_guest_paid_email') || '').trim().toLowerCase() || null;
      return { service: 'waste_management', requestId, email };
    }
    return null;
  }

  const requestId = (params.get('request_id') || params.get('order') || params.get('job') || readSession(SESSION_ID_KEY) || '').trim() || null;
  let email = (params.get('email') || '').trim();
  if (!email) {
    for (const key of SESSION_EMAIL_KEYS) {
      const v = (readSession(key) || '').trim();
      if (v) { email = v; break; }
    }
  }

  return {
    service: normalizePaidService(params.get('service')),
    requestId,
    email: email.toLowerCase() || null,
  };
}

/**
 * Drop the params from the address bar so a reload does not re-open the
 * receipt over whatever the customer navigated to next.
 */
export function clearGuestPaidReturnParams(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) return;
  try {
    const u = new URL(window.location.href);
    for (const k of ['guest_paid', 'water_paid', 'wm_paid', 'request_id', 'session_id', 'service', 'email', 'order', 'job', 'open']) {
      u.searchParams.delete(k);
    }
    window.history.replaceState({}, '', u.toString());
  } catch { /* address bar is cosmetic — never break the page over it */ }
}

/** Capacitor / localhost origins cannot be Stripe success URLs. */
export function publicWebOrigin(): string {
  if (typeof window === 'undefined') return PUBLIC_WEB_ORIGIN;
  const origin = window.location.origin || '';
  if (!origin || /^(capacitor|ionic):/i.test(origin) || /localhost/i.test(origin)) {
    return PUBLIC_WEB_ORIGIN;
  }
  return origin;
}

export function guestPaidSearchFromHref(href: string): string {
  const raw = String(href || '').trim();
  if (!raw) return '';
  if (raw.startsWith('?')) return raw;
  try {
    return new URL(raw).search;
  } catch {
    const q = raw.indexOf('?');
    return q >= 0 ? raw.slice(q) : '';
  }
}

/** Stripe success / cancel URLs. Native uses an HTTPS bounce so Checkout accepts them. */
export function buildGuestPaidReturnUrls(opts: {
  service: string;
  email?: string | null;
  requestId?: string | null;
}): { successUrl: string; cancelUrl: string } {
  const service = String(opts.service || '').trim() || 'hotshots';
  const success = new URLSearchParams();
  success.set('guest_paid', '1');
  success.set('service', service);
  const email = String(opts.email || '').trim();
  if (email) success.set('email', email);
  const requestId = String(opts.requestId || '').trim();
  if (requestId) success.set('request_id', requestId);

  const cancel = new URLSearchParams();
  cancel.set('guest_paid', '0');
  cancel.set('service', service);
  if (requestId) cancel.set('request_id', requestId);

  if (isIosNativeAppRuntime()) {
    const bounce = `${PUBLIC_WEB_ORIGIN}${GUEST_PAID_BOUNCE_PATH}`;
    return {
      successUrl: `${bounce}?${success.toString()}`,
      cancelUrl: `${bounce}?${cancel.toString()}`,
    };
  }
  const origin = publicWebOrigin();
  return {
    successUrl: `${origin}/?${success.toString()}`,
    cancelUrl: `${origin}/?${cancel.toString()}`,
  };
}

export function parseGuestPaidHref(href: string): GuestPaidReturn | null {
  return readGuestPaidReturn(guestPaidSearchFromHref(href));
}

export function isGuestPaidCancelHref(href: string): boolean {
  try {
    return new URLSearchParams(guestPaidSearchFromHref(href)).get('guest_paid') === '0';
  } catch {
    return false;
  }
}

export function persistGuestPaidSession(hit: GuestPaidReturn): void {
  try {
    if (hit.requestId) sessionStorage.setItem(SESSION_ID_KEY, hit.requestId);
    if (hit.email) {
      sessionStorage.setItem('zia_guest_paid_email', hit.email);
      const service = hit.service;
      if (service === 'grocery') sessionStorage.setItem('zia_guest_grocery_email', hit.email);
      if (service === 'tacos') sessionStorage.setItem('zia_guest_food_email', hit.email);
      if (service === 'hotshots' || service === 'semis') sessionStorage.setItem('zia_guest_hotshot_email', hit.email);
      if (service === 'water') sessionStorage.setItem('zia_guest_water_email', hit.email);
      if (service === 'waste_management') sessionStorage.setItem('zia_guest_wm_email', hit.email);
      if (service === 'semis') sessionStorage.setItem('zia_guest_semi_email', hit.email);
      if (service === 'welding') sessionStorage.setItem('zia_guest_welding_email', hit.email);
      if (service === 'porta_potty') sessionStorage.setItem('zia_guest_porta_email', hit.email);
    }
  } catch { /* sessionStorage can be blocked */ }
}

/**
 * Fire-and-forget GET /api/service-requests/receipt so a missed Stripe webhook
 * still promotes create-after-pay (e.g. porta_checkout_pending → live Desk).
 * Operator Desk paid-return opens Desk instead of the service hub, so this
 * must run even when canPostOperatorBoard short-circuits hub mount.
 * Uses the guest checkout request id (not a pp_/vertical twin) + checkout email.
 */
export function reconcileGuestPaidReceipt(
  requestId: string | null | undefined,
  email: string | null | undefined,
): void {
  const id = String(requestId || '').trim();
  const em = String(email || '').trim().toLowerCase();
  if (!id || !em || typeof fetch !== 'function') return;
  void fetch(
    `/api/service-requests/receipt?requestId=${encodeURIComponent(id)}&email=${encodeURIComponent(em)}`,
    { headers: { Accept: 'application/json' }, cache: 'no-store' },
  ).catch(() => { /* reconcile is best-effort; Desk still opens */ });
}

export function dispatchGuestPaidReturn(hit: GuestPaidReturn): void {
  if (typeof window === 'undefined') return;
  persistGuestPaidSession(hit);
  window.dispatchEvent(new CustomEvent(ZIA_GUEST_PAID_EVENT, { detail: hit }));
}

export function dispatchGuestPaidCancel(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ZIA_GUEST_PAID_CANCEL_EVENT));
}

export function subscribeGuestPaidReturn(cb: (hit: GuestPaidReturn) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (ev: Event) => {
    const hit = (ev as CustomEvent<GuestPaidReturn>).detail;
    if (hit && hit.service) cb(hit);
  };
  window.addEventListener(ZIA_GUEST_PAID_EVENT, handler);
  return () => window.removeEventListener(ZIA_GUEST_PAID_EVENT, handler);
}

export function subscribeGuestPaidCancel(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => cb();
  window.addEventListener(ZIA_GUEST_PAID_CANCEL_EVENT, handler);
  return () => window.removeEventListener(ZIA_GUEST_PAID_CANCEL_EVENT, handler);
}
